import { useState } from "react";

/**
 * Custom hook for handling delete confirmations
 * Provides a consistent delete confirmation experience across the app
 *
 * @returns {Object} An object containing confirmDelete function and modal state
 */
export const useDeleteConfirmation = () => {
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Show a confirmation dialog and execute the delete action if confirmed
   *
   * @param {Function} deleteAction - The async function to execute if user confirms
   * @param {string} confirmMessage - Custom confirmation message (optional)
   * @returns {Promise<boolean>} - Returns true if deletion was successful, false otherwise
   */
  const confirmDelete = async (
    deleteAction,
    confirmMessage = "Are you sure you want to delete this item?",
  ) => {
    if (!window.confirm(confirmMessage)) {
      return false;
    }

    setIsDeleting(true);
    try {
      await deleteAction();
      return true;
    } catch (error) {
      console.error("Error during delete operation:", error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    confirmDelete,
    isDeleting,
  };
};
